import React, { useState, useEffect } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Target, Calendar, Loader2 } from 'lucide-react';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const AdminChallenges = () => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const currentDate = new Date();
  const [form, setForm] = useState({
    name: '',
    description: '',
    month: currentDate.getMonth() + 1,
    year: currentDate.getFullYear(),
    tip_set: 1,
    is_active: true
  });

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      const res = await apiClient.get('/challenges');
      setChallenges(res.data);
    } catch (err) {
      toast.error('Failed to fetch challenges');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const now = new Date();
    setForm({
      name: '',
      description: '28 days of building habits that boost metabolism, build muscle, burn fat, and supercharge your energy!',
      month: now.getMonth() + 1,
      year: now.getFullYear(),
      tip_set: 1,
      is_active: true
    });
    setEditing(null);
  };

  const openEdit = (challenge) => {
    setForm({
      name: challenge.name,
      description: challenge.description,
      month: challenge.month,
      year: challenge.year,
      tip_set: challenge.tip_set,
      is_active: challenge.is_active
    });
    setEditing(challenge.id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error('Please enter a challenge name');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await apiClient.put(`/challenges/${editing}`, form);
        toast.success('Challenge updated');
      } else {
        await apiClient.post('/challenges', form);
        toast.success('Challenge created');
      }
      fetchChallenges();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save challenge');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this challenge?')) return;
    
    try {
      await apiClient.delete(`/challenges/${id}`);
      toast.success('Challenge deleted');
      fetchChallenges();
    } catch (err) {
      toast.error('Failed to delete challenge');
    }
  };

  const getMonthName = (monthNum) => {
    return MONTHS.find(m => m.value === monthNum)?.label || monthNum;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900">MONTHLY CHALLENGES</h1>
            <p className="text-stone-600 text-sm">Challenge runs 1st-28th each month. Create one per month.</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold" data-testid="add-challenge-btn">
                <Plus size={18} className="mr-2" />
                Add Challenge
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-stone-200 text-stone-900 max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  {editing ? 'Edit Challenge' : 'Create Challenge'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="RxGRIT January Challenge"
                    className="bg-stone-50 border-stone-200 mt-1"
                    data-testid="challenge-name-input"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Challenge description..."
                    className="bg-stone-50 border-stone-200 mt-1"
                    rows={3}
                    data-testid="challenge-description-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Month *</Label>
                    <Select value={form.month.toString()} onValueChange={(v) => setForm({ ...form, month: parseInt(v) })}>
                      <SelectTrigger className="bg-stone-50 border-stone-200 mt-1" data-testid="challenge-month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-stone-200">
                        {MONTHS.map(m => (
                          <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Year *</Label>
                    <Input
                      type="number"
                      min="2024"
                      max="2030"
                      value={form.year}
                      onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })}
                      className="bg-stone-50 border-stone-200 mt-1"
                      data-testid="challenge-year-input"
                    />
                  </div>
                  <div>
                    <Label>Tip Set</Label>
                    <Select value={form.tip_set.toString()} onValueChange={(v) => setForm({ ...form, tip_set: parseInt(v) })}>
                      <SelectTrigger className="bg-stone-50 border-stone-200 mt-1" data-testid="challenge-tipset-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-stone-200">
                        <SelectItem value="1">Set 1</SelectItem>
                        <SelectItem value="2">Set 2</SelectItem>
                        <SelectItem value="3">Set 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Label>Active</Label>
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                    data-testid="challenge-active-switch"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold"
                  data-testid="challenge-submit-btn"
                >
                  {saving ? <Loader2 className="animate-spin" size={18} /> : editing ? 'Update' : 'Create'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Info Banner */}
        <div className="bg-white border border-stone-200 rounded-xl p-4 mb-6">
          <p className="text-stone-600 text-sm">
            <strong className="text-stone-900">How it works:</strong> Each challenge runs from the 1st to the 28th of its month. 
            Users see the same daily content based on the calendar date. Tip Sets (1, 2, 3) allow you to rotate content each month for variety.
          </p>
        </div>

        {/* Challenges List */}
        <div className="space-y-4">
          {challenges.length > 0 ? (
            challenges.map((challenge) => (
              <div
                key={challenge.id}
                className={`bg-white border rounded-2xl p-6 ${
                  challenge.is_active ? 'border-[#EFB342]/50' : 'border-stone-200'
                }`}
                data-testid={`challenge-${challenge.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      challenge.is_active ? 'bg-[#EFB342]/20' : 'bg-stone-100'
                    }`}>
                      <Target className={challenge.is_active ? 'text-[#EFB342]' : 'text-stone-500'} size={24} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-stone-900">{challenge.name}</h3>
                        {challenge.is_active && (
                          <span className="px-2 py-0.5 bg-[#EFB342]/20 text-[#EFB342] text-xs rounded-full font-medium">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-stone-600 text-sm mt-1">{challenge.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-stone-500">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {getMonthName(challenge.month)} {challenge.year} (1st - 28th)
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(challenge)}
                      className="text-stone-600 hover:text-stone-900"
                      data-testid={`edit-challenge-${challenge.id}`}
                    >
                      <Pencil size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(challenge.id)}
                      className="text-stone-600 hover:text-red-500"
                      data-testid={`delete-challenge-${challenge.id}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>

                {/* Resource Links */}
                <div className="mt-4 pt-4 border-t border-stone-200 grid sm:grid-cols-2 gap-4">
                  <a 
                    href="https://buildnutrition.ca/rxgrit-toolbox" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors group"
                  >
                    <h4 className="font-semibold text-stone-900 group-hover:text-[#EFB342] transition-colors">
                      Access the Toolbox
                    </h4>
                    <p className="text-stone-600 text-sm mt-1">
                      Meal plans, recipes, macro guides, templates and more - DIY resources to crush the monthly challenges 👊🏼
                    </p>
                  </a>
                  <a 
                    href="https://p.bttr.to/3LHEaO9" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors group"
                  >
                    <h4 className="font-semibold text-stone-900 group-hover:text-[#EFB342] transition-colors">
                      Book 1:1 Support
                    </h4>
                    <p className="text-stone-600 text-sm mt-1">
                      DIY most days, 50% OFF coaching when needed [use discount code RxGRIT50]
                    </p>
                  </a>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16 bg-white border border-stone-200 rounded-2xl">
              <Target className="mx-auto text-stone-400 mb-4" size={48} />
              <p className="text-stone-500">No challenges yet</p>
              <p className="text-stone-400 text-sm">Create your first monthly challenge to get started</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminChallenges;
